import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import {
  BsBank,
  BsBuilding,
  BsCheck2,
  BsEnvelope,
  BsEye,
  BsEyeSlash,
  BsGeoAlt,
  BsGrid,
  BsImage,
  BsKey,
  BsPeople,
  BsPerson,
  BsShieldCheck,
  BsTelephone,
  BsX,
} from 'react-icons/bs';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ApiConfig } from '../constants/apiConfig';
import { guruAuthorizationHeaders, logoutGuru } from '../services/authService';
import logo from '../assets/EPRAJOGO1.png';
import {
  changeGuruPassword,
  decideGuruJournal,
  decideGuruPermit,
  fetchGuruPermitPhoto,
  fetchGuruDashboard,
  fetchGuruJournals,
  fetchGuruPermits,
  fetchGuruStudents,
  fetchStudentActivity,
  moveStudentDevice,
  resetStudentPassword,
  resetStudentSchedule,
} from '../services/guruService';

const cardStyle = { border: '1px solid #E1E8F0', borderRadius: 16, boxShadow: '0 4px 14px #102e5010' };
const currentMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
};

function resolveJournalPhotoUrl(url) {
  if (!url) return '';
  try {
    const photo = new URL(url, window.location.origin);
    if (photo.pathname.startsWith('/jurnal/')) {
      return `${window.location.origin}${photo.pathname}${photo.search}`;
    }
  } catch (_) {
    return url;
  }
  return url;
}

function messageOf(error) {
  const value = error?.response?.data?.message || error?.message || 'Terjadi kesalahan. Coba lagi.';
  return typeof value === 'string' ? value : Object.values(value).flat().filter(Boolean).join(' ');
}

function StatusBadge({ status }) {
  const variant = status === 'hadir' || status === 'disetujui' || status === 'izin'
    ? 'success'
    : status === 'ditolak'
      ? 'danger'
      : status === 'terlambat' || status === 'perlu_revisi'
        ? 'warning'
        : 'secondary';
  const label = {
    hadir: 'Hadir', menunggu: 'Menunggu', izin: 'Disetujui', sakit: 'Sakit',
    ditolak: 'Ditolak', terlambat: 'Terlambat', disetujui: 'Disetujui',
    perlu_revisi: 'Perlu revisi',
  }[status] || status || 'Belum presensi';
  return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{label}</Badge>;
}

function AccountInfoRow({ icon: Icon, label, value, unavailable = false }) {
  return (
    <div className="guru-account-info-row">
      <span className="guru-account-info-icon"><Icon aria-hidden="true" /></span>
      <span className="guru-account-info-copy">
        <small>{label}</small>
        <strong className={unavailable ? 'unavailable' : ''}>{value}</strong>
      </span>
    </div>
  );
}

function PasswordField({ label, value, visible, onChange, onToggle, autoComplete }) {
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <InputGroup className="guru-password-input">
        <Form.Control
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required
        />
        <Button
          type="button"
          variant="outline-secondary"
          aria-label={visible ? `Sembunyikan ${label.toLowerCase()}` : `Lihat ${label.toLowerCase()}`}
          onClick={onToggle}
        >
          {visible ? <BsEyeSlash /> : <BsEye />}
        </Button>
      </InputGroup>
    </Form.Group>
  );
}

function GuruPortal() {
  const [activeTab, setActiveTab] = useState('monitoring');
  const [settingsTab, setSettingsTab] = useState('kehadiran');
  const [dashboard, setDashboard] = useState({});
  const [students, setStudents] = useState([]);
  const [resetStudents, setResetStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityMonth, setActivityMonth] = useState(currentMonth);
  const [activityError, setActivityError] = useState('');
  const activityRequest = useRef(0);
  const monitoringRequest = useRef(0);
  const [permits, setPermits] = useState([]);
  const [permitPhoto, setPermitPhoto] = useState(null);
  const [photoLoadingId, setPhotoLoadingId] = useState('');
  const permitPhotoUrl = useRef(null);
  const [permitStatus, setPermitStatus] = useState('menunggu');
  const [journalKind, setJournalKind] = useState('harian');
  const [journalStatus, setJournalStatus] = useState('menunggu');
  const [journals, setJournals] = useState([]);
  const [permitSearch, setPermitSearch] = useState('');
  const [journalSearch, setJournalSearch] = useState('');
  const [queuePage, setQueuePage] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [queueLoading, setQueueLoading] = useState(false);
  const settingsRequest = useRef(0);
  const [studentId, setStudentId] = useState('');
  const [revisionNotes, setRevisionNotes] = useState({});
  const [message, setMessage] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const actionInFlight = useRef(false);
  const [profile, setProfile] = useState({
    nama_guru: localStorage.getItem('guru_nama') || 'Guru Pembimbing',
    nip: localStorage.getItem('guru_nip') || '-',
    role: 'guru',
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisibility, setPasswordVisibility] = useState([false, false, false]);

  const selectedStudentData = useMemo(
    () => resetStudents.find((student) => String(student.id) === String(studentId)),
    [resetStudents, studentId]
  );

  const loadMonitoring = useCallback(async (term = '') => {
    const requestId = ++monitoringRequest.current;
    try {
      const [summary, list] = await Promise.all([
        fetchGuruDashboard(),
        fetchGuruStudents(term),
      ]);
      if (requestId !== monitoringRequest.current) return;
      setDashboard(summary);
      setStudents(list);
      setStudentId((previous) => previous || (list[0] ? String(list[0].id) : ''));
    } catch (error) {
      if (requestId === monitoringRequest.current) setMessage({ type: 'danger', text: messageOf(error) });
    }
  }, []);

  const loadSettingsData = useCallback(async (page = 1) => {
    const requestId = ++settingsRequest.current;
    setQueueLoading(true);
    if (page === 1) {
      setPermits([]);
      setJournals([]);
      setQueuePage({ current_page: 1, last_page: 1, total: 0 });
      if (settingsTab === 'reset') setResetStudents([]);
    }
    try {
      if (settingsTab === 'reset') {
        const list = await fetchGuruStudents();
        if (requestId !== settingsRequest.current) return;
        setResetStudents(list);
        setStudentId((previous) => list.some((student) => String(student.id) === previous) ? previous : String(list[0]?.id || ''));
      } else {
        const result = settingsTab === 'kehadiran'
          ? await fetchGuruPermits(permitStatus, page, permitSearch)
          : await fetchGuruJournals(journalKind, journalStatus, page, journalSearch);
        if (requestId !== settingsRequest.current) return;
        const setItems = settingsTab === 'kehadiran' ? setPermits : setJournals;
        setItems((previous) => page === 1 ? result.data : [...previous, ...result.data]);
        setQueuePage(result);
      }
    } catch (error) {
      if (requestId === settingsRequest.current) setMessage({ type: 'danger', text: messageOf(error) });
    } finally {
      if (requestId === settingsRequest.current) setQueueLoading(false);
    }
  }, [settingsTab, permitStatus, journalKind, journalStatus, permitSearch, journalSearch]);
  const latestContext = useRef();
  latestContext.current = { activeTab, search, loadSettingsData };

  useEffect(() => {
    loadMonitoring();
    axios.get(`${ApiConfig.baseUrl}/guru/profile`, { headers: guruAuthorizationHeaders() })
      .then((response) => {
        const teacher = response.data?.data || {};
        setProfile(teacher);
        localStorage.setItem('guru_nama', teacher.nama_guru || 'Guru Pembimbing');
        localStorage.setItem('guru_nip', teacher.nip || '-');
      })
      .catch(() => {});
  }, [loadMonitoring]);

  useEffect(() => {
    if (activeTab === 'setting') loadSettingsData();
    return () => { settingsRequest.current += 1; };
  }, [activeTab, loadSettingsData]);

  useEffect(() => {
    if (!selectedStudent) return;
    const requestId = ++activityRequest.current;
    setActivity(null);
    setActivityError('');
    fetchStudentActivity(selectedStudent.id, activityMonth)
      .then((data) => { if (requestId === activityRequest.current) setActivity(data); })
      .catch((error) => { if (requestId === activityRequest.current) setActivityError(messageOf(error)); });
    return () => { activityRequest.current += 1; };
  }, [selectedStudent, activityMonth]);

  const openActivity = (student) => {
    setActivityMonth(currentMonth());
    setSelectedStudent(student);
  };

  const closeActivity = () => {
    setSelectedStudent(null);
    setActivity(null);
    setActivityError('');
  };

  const runAction = async (key, action, successText) => {
    if (actionInFlight.current) return null;
    actionInFlight.current = true;
    setBusyKey(key);
    setMessage(null);
    try {
      const result = await action();
      setMessage({ type: 'success', text: result?.message || successText });
      await loadMonitoring(latestContext.current.search);
      if (latestContext.current.activeTab === 'setting') await latestContext.current.loadSettingsData();
      return result;
    } catch (error) {
      setMessage({ type: 'danger', text: messageOf(error) });
      return null;
    } finally {
      actionInFlight.current = false;
      setBusyKey('');
    }
  };

  const handlePermitDecision = (permit, status) => {
    const verb = status === 'disetujui' ? 'setujui' : 'tolak';
    if (!window.confirm(`Yakin ${verb} pengajuan ${permit.jenis_izin} dari ${permit.siswa?.nama}?`)) return;
    runAction(`permit-${permit.id}`, () => decideGuruPermit(permit.id, status), 'Status izin diperbarui.');
  };

  const openPermitPhoto = async (permit) => {
    setPhotoLoadingId(String(permit.id));
    try {
      const image = await fetchGuruPermitPhoto(permit.id);
      if (permitPhotoUrl.current) URL.revokeObjectURL(permitPhotoUrl.current);
      permitPhotoUrl.current = URL.createObjectURL(image);
      setPermitPhoto({ url: permitPhotoUrl.current, permit });
    } catch (error) {
      setMessage({ type: 'danger', text: messageOf(error) });
    } finally {
      setPhotoLoadingId('');
    }
  };

  const closePermitPhoto = () => {
    if (permitPhotoUrl.current) URL.revokeObjectURL(permitPhotoUrl.current);
    permitPhotoUrl.current = null;
    setPermitPhoto(null);
  };

  useEffect(() => () => {
    if (permitPhotoUrl.current) URL.revokeObjectURL(permitPhotoUrl.current);
  }, []);

  const handleJournalDecision = (journal, status) => {
    const note = revisionNotes[journal.id]?.trim() || '';
    if (status === 'perlu_revisi' && !note) {
      setMessage({ type: 'warning', text: 'Catatan revisi wajib diisi.' });
      return;
    }
    const verb = status === 'disetujui' ? 'setujui' : 'kirim kembali untuk revisi';
    if (!window.confirm(`Yakin ${verb} jurnal ${journal.jenis} milik ${journal.siswa?.nama}?`)) return;
    runAction(`journal-${journal.id}`, () => decideGuruJournal(journal.jenis, journal.id, status, note), 'Jurnal berhasil diverifikasi.');
  };

  const handleLogout = async () => {
    if (!window.confirm('Keluar dari akun Guru?')) return;
    await logoutGuru();
    window.location.href = '/login';
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 8) {
      setPasswordError('Kata sandi baru minimal 8 karakter.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Kata sandi baru belum cocok.');
      return;
    }
    setPasswordError('');
    setBusyKey('password');
    try {
      const response = await changeGuruPassword(currentPassword, newPassword);
      setMessage({ type: 'success', text: response.message || 'Kata sandi diperbarui.' });
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordVisibility([false, false, false]);
    } catch (error) {
      setPasswordError(messageOf(error));
    } finally {
      setBusyKey('');
    }
  };

  const openPasswordModal = () => {
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (busyKey === 'password') return;
    setShowPasswordModal(false);
    setPasswordError('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordVisibility([false, false, false]);
  };

  const statItems = [
    ['Siswa bimbingan', dashboard.jumlah_siswa],
    ['Hadir hari ini', dashboard.siswa_hadir_hari_ini],
    ['Izin hari ini', dashboard.siswa_izin_hari_ini],
    ['Menunggu presensi', dashboard.siswa_belum_presensi_hari_ini],
    ['Izin menunggu', dashboard.izin_menunggu],
    ['Jurnal hari ini', dashboard.jurnal_hari_ini],
  ];

  return (
    <main className="guru-app-shell">
      <header className={`guru-app-header ${activeTab === 'akun' ? 'guru-account-header' : ''}`}>
        <div className="guru-header-inner">
          <img src={logo} alt="Logo E-Prajogo" className="app-brand-logo" />
          {activeTab === 'akun' ? (
            <div className="flex-grow-1">
              <div className="guru-account-header-title">Pengaturan Akun</div>
              <div className="guru-account-header-subtitle">Profil dan keamanan akun</div>
            </div>
          ) : <>
            <div className="flex-grow-1">
              <p className="app-brand-name">E-PRAJOGO</p>
              <p className="app-brand-school">SMK NEGERI GUDO</p>
            </div>
            <span className="guru-role-pill">PEMBIMBING</span>
          </>}
        </div>
        {activeTab !== 'akun' && <>
          <div className="guru-header-title">{activeTab === 'monitoring' ? 'Monitoring Siswa' : 'Setting Siswa'}</div>
          <div className="guru-header-subtitle">{activeTab === 'monitoring' ? `${profile.nama_guru || 'Guru Pembimbing'} • ${new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}` : 'Kelola verifikasi dan data siswa'}</div>
        </>}
      </header>

      <div className="guru-page-content">
        {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
        {activeTab === 'monitoring' && (
          <>
            <h5 className="fw-bold mb-3">Ringkasan Monitoring</h5>
            <div className="row g-2 mb-3">
              {statItems.map(([label, value]) => (
                <div className="col-6 col-md-4" key={label}>
                  <Card style={cardStyle}><Card.Body className="py-3">
                    <div className="fs-4 fw-bold text-primary">{value ?? '—'}</div>
                    <small className="text-muted">{label}</small>
                  </Card.Body></Card>
                </div>
              ))}
            </div>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
              <h5 className="fw-bold mb-0">Siswa Bimbingan</h5>
              <Button variant="outline-primary" size="sm" onClick={() => loadMonitoring(search)}>Muat ulang</Button>
            </div>
            <Form.Control className="mb-3" aria-label="Cari siswa bimbingan" maxLength={120} placeholder="Cari nama, NISN, kelas, atau DUDI" value={search}
              onChange={(event) => { setSearch(event.target.value); loadMonitoring(event.target.value); }} />
            <div className="d-grid gap-2">
              {students.map((student) => (
                <Card key={student.id} style={cardStyle}>
                  <Card.Body className="guru-student-row">
                    <div className="rounded-circle bg-primary-subtle text-primary fw-bold d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                      {(student.nama || 'S').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-bold">{student.nama}</div>
                      <small className="text-muted">{student.kelas} • NISN {student.nisn}</small>
                      <div><small className="text-muted">{student.tempat_pkl || 'DUDI belum diatur'}</small></div>
                    </div>
                    <StatusBadge status={student.status_presensi_hari_ini} />
                    <Button size="sm" variant="outline-primary" onClick={() => openActivity(student)}>Detail</Button>
                  </Card.Body>
                </Card>
              ))}
              {!students.length && <div className="text-center text-muted py-4">Belum ada siswa bimbingan.</div>}
            </div>
            <Modal
              show={Boolean(selectedStudent)}
              onHide={closeActivity}
              centered
              scrollable
              dialogClassName="guru-activity-modal"
            >
              <Modal.Header closeButton>
                <div className="min-w-0">
                  <Modal.Title className="fs-6 fw-bold">{activity?.siswa?.nama || selectedStudent?.nama}</Modal.Title>
                  {selectedStudent && <div className="small text-muted mt-1">{activity?.siswa?.kelas || selectedStudent.kelas} • {activity?.siswa?.tempat_pkl || selectedStudent.tempat_pkl || 'DUDI belum diatur'}</div>}
                </div>
              </Modal.Header>
              <Modal.Body>
                {selectedStudent && <>
                  <Form.Group className="my-3" controlId="activity-month">
                    <Form.Label>Bulan aktivitas</Form.Label>
                    <Form.Control type="month" value={activityMonth} onChange={(event) => { if (event.target.value) setActivityMonth(event.target.value); }} />
                  </Form.Group>
                  {activityError ? <Alert variant="danger">{activityError}</Alert> : !activity ? <div className="text-center py-3"><Spinner size="sm" /> Memuat aktivitas...</div> : <>
                    <div className="row g-2 my-3">
                      {Object.entries({ Hadir: activity.statistik?.hadir, 'Izin/Sakit': activity.statistik?.izin_sakit, Terlambat: activity.statistik?.terlambat, 'Kehadiran %': activity.statistik?.persentase_kehadiran }).map(([label, value]) => <div className="col-6 col-md-3" key={label}><div className="p-2 rounded bg-light"><strong>{value ?? 0}</strong><small className="d-block text-muted">{label}</small></div></div>)}
                    </div>
                    <h6 className="fw-bold">Riwayat Presensi</h6>
                    <div className="d-grid gap-2 mb-3">{(activity.riwayat_presensi || []).map((item) => <div key={item.tanggal} className="d-flex justify-content-between gap-2 border-bottom py-2"><span>{item.tanggal} · {item.masuk || '—'} / {item.keluar || '—'}</span><StatusBadge status={item.status} /></div>)}{!activity.riwayat_presensi?.length && <p className="small text-muted">Belum ada presensi pada bulan ini.</p>}</div>
                    <Button as={Link} to={`/guru/penilaian/${selectedStudent.id}`} className="w-100 mb-3">Penilaian PKL & Sertifikat</Button>
                    <h6 className="fw-bold">Jurnal Terbaru</h6>
                    {[...(activity.jurnal_harian || [])].slice(0, 3).map((journal) => <div key={`h-${journal.id}`} className="border-bottom py-2"><strong>{journal.tanggal}</strong> · {journal.pekerjaan} <StatusBadge status={journal.status_verifikasi} />{journal.catatan_revisi && <small className="d-block text-warning">Catatan: {journal.catatan_revisi}</small>}</div>)}
                    {[...(activity.jurnal_mingguan || [])].slice(0, 3).map((journal) => <div key={`m-${journal.id}`} className="border-bottom py-2"><strong>Minggu {journal.minggu_ke}</strong> · {journal.nama_pekerjaan} <StatusBadge status={journal.status_verifikasi} />{journal.catatan_revisi && <small className="d-block text-warning">Catatan: {journal.catatan_revisi}</small>}</div>)}
                  </>}
                </>}
              </Modal.Body>
            </Modal>
          </>
        )}

        {activeTab === 'setting' && (
          <>
            <h5 className="fw-bold mb-3">Setting Siswa</h5>
            <div className="btn-group w-100 mb-3" role="group" aria-label="Pengaturan siswa">
              {[
                ['kehadiran', 'Verifikasi Kehadiran'], ['jurnal', 'Verifikasi Jurnal'], ['reset', 'Reset Data'],
              ].map(([key, label]) => <Button key={key} size="sm" variant={settingsTab === key ? 'primary' : 'outline-primary'} onClick={() => setSettingsTab(key)}>{label}</Button>)}
            </div>

            {settingsTab === 'kehadiran' && <>
              <div className="permit-queue-banner mb-3">
                <div>
                  <div className="fw-bold">{permitStatus === 'menunggu' ? 'Pengajuan Menunggu' : 'Riwayat Verifikasi'}</div>
                  <small>{permitStatus === 'menunggu' ? 'Periksa alasan dan bukti sebelum memberi keputusan.' : 'Izin dan sakit yang sudah diproses.'}</small>
                </div>
                <strong>{queuePage.total} {permitStatus === 'menunggu' ? 'pengajuan' : 'tercatat'}</strong>
              </div>
              <div className="permit-status-switch mb-3" role="group" aria-label="Status pengajuan izin">
                {['menunggu', 'riwayat'].map((status) => <button key={status} type="button" className={permitStatus === status ? 'active' : ''} aria-pressed={permitStatus === status} onClick={() => setPermitStatus(status)}>{status === 'menunggu' ? 'Menunggu' : 'Riwayat'}</button>)}
              </div>
              <Form.Control className="mb-3" aria-label="Cari pengajuan izin" maxLength={120} placeholder="Cari siswa atau alasan izin" value={permitSearch} onChange={(event) => setPermitSearch(event.target.value)} />
              <div className="d-grid gap-3">
                {permits.map((permit) => <Card key={permit.id} className="guru-content-card permit-card" style={cardStyle}><Card.Body>
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-3">
                      <div className={`permit-avatar ${permit.jenis_izin === 'sakit' ? 'sick' : ''}`}>{(permit.siswa?.nama || 'S').trim().slice(0, 1).toUpperCase()}</div>
                      <div><strong>{permit.siswa?.nama || 'Siswa'}</strong><div className="text-muted small">{permit.siswa?.kelas || 'Kelas —'} • {permit.tanggal_izin || permit.tanggal}</div></div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-2">
                      <Badge bg={permit.jenis_izin === 'sakit' ? 'warning' : 'primary'} text={permit.jenis_izin === 'sakit' ? 'dark' : undefined}>{permit.jenis_izin === 'sakit' ? 'Sakit' : 'Izin'}</Badge>
                      <StatusBadge status={permit.status === 'izin' ? 'disetujui' : permit.status} />
                    </div>
                  </div>
                  <div className="permit-reason-block">
                    <small className="text-muted fw-semibold">Alasan</small>
                    <p className="mb-0 mt-1">{permit.keterangan || 'Tidak ada keterangan.'}</p>
                  </div>
                  {permit.foto_izin_url && <Button type="button" variant="light" className="permit-photo-button w-100 mt-3" disabled={photoLoadingId === String(permit.id)} onClick={() => openPermitPhoto(permit)}>
                    {photoLoadingId === String(permit.id) ? <Spinner size="sm" className="me-2" /> : <BsImage className="me-2 text-primary" />}
                    {photoLoadingId === String(permit.id) ? 'Membuka bukti...' : 'Lihat lampiran bukti'}
                  </Button>}
                  {permit.status === 'menunggu' && <div className="d-flex gap-2 mt-3"><Button className="flex-fill" variant="danger" disabled={busyKey === `permit-${permit.id}`} onClick={() => handlePermitDecision(permit, 'ditolak')}><BsX className="me-1" />Tolak</Button><Button className="flex-fill" disabled={busyKey === `permit-${permit.id}`} onClick={() => handlePermitDecision(permit, 'disetujui')}><BsCheck2 className="me-1" />Setujui</Button></div>}
                </Card.Body></Card>)}
                {!queueLoading && !permits.length && <div className="text-center text-muted py-4">Tidak ada pengajuan pada daftar ini.</div>}
              </div>
            </>}

            {settingsTab === 'jurnal' && <>
              <div className="d-flex gap-2 mb-2">{[['harian', 'Harian'], ['mingguan', 'Mingguan']].map(([kind, label]) => <Button key={kind} size="sm" variant={journalKind === kind ? 'primary' : 'outline-primary'} onClick={() => { setJournalKind(kind); setRevisionNotes({}); }}>{label}</Button>)}</div>
              <div className="d-flex gap-2 mb-3">{[['menunggu', 'Menunggu'], ['riwayat', 'Riwayat']].map(([status, label]) => <Button key={status} size="sm" variant={journalStatus === status ? 'primary' : 'outline-secondary'} onClick={() => setJournalStatus(status)}>{label}</Button>)}</div>
              <Form.Control className="mb-3" aria-label="Cari jurnal siswa" maxLength={120} placeholder="Cari siswa atau kegiatan jurnal" value={journalSearch} onChange={(event) => setJournalSearch(event.target.value)} />
              <div className="d-grid gap-3">
                {journals.map((journal) => <Card key={journal.id} style={cardStyle}><Card.Body>
                  <div className="d-flex justify-content-between gap-2"><div><strong>{journal.siswa?.nama}</strong><div className="small text-muted">{journal.siswa?.kelas} • {journal.jenis === 'mingguan' ? `Minggu ${journal.minggu_ke}` : journal.tanggal}</div></div><StatusBadge status={journal.status_verifikasi} /></div>
                  <h6 className="fw-bold mt-3 mb-1">{journal.pekerjaan}</h6><p className="text-muted mb-2">{journal.catatan}</p>
                  {!!journal.foto?.length && <div className="d-flex gap-2 overflow-auto mb-2">{journal.foto.map((photo, index) => {
                    const photoUrl = resolveJournalPhotoUrl(photo.url);
                    return <a key={`${journal.id}-${index}`} href={photoUrl} target="_blank" rel="noreferrer"><img src={photoUrl} alt={photo.deskripsi || 'Dokumentasi jurnal'} style={{ width: 112, height: 90, objectFit: 'cover', borderRadius: 8 }} /></a>;
                  })}</div>}
                  {journal.status_verifikasi === 'menunggu' && <>
                    <Form.Control as="textarea" rows={2} className="mb-2" placeholder="Catatan wajib jika meminta revisi" value={revisionNotes[journal.id] || ''} onChange={(event) => setRevisionNotes((state) => ({ ...state, [journal.id]: event.target.value }))} />
                    <div className="d-flex gap-2"><Button className="flex-fill" variant="warning" disabled={busyKey === `journal-${journal.id}`} onClick={() => handleJournalDecision(journal, 'perlu_revisi')}>Minta Revisi</Button><Button className="flex-fill" disabled={busyKey === `journal-${journal.id}`} onClick={() => handleJournalDecision(journal, 'disetujui')}>Setujui</Button></div>
                  </>}
                </Card.Body></Card>)}
                {!queueLoading && !journals.length && <div className="text-center text-muted py-4">Tidak ada jurnal pada daftar ini.</div>}
              </div>
            </>}

            {settingsTab !== 'reset' && <div className="text-center my-3">
              {queueLoading ? <span role="status"><Spinner size="sm" className="me-2" />Memuat daftar...</span> : queuePage.current_page < queuePage.last_page && <Button variant="outline-primary" onClick={() => loadSettingsData(queuePage.current_page + 1)}>Tampilkan Lebih Banyak</Button>}
            </div>}

            {settingsTab === 'reset' && <>
              <Form.Label className="fw-bold" htmlFor="reset-student">Pilih siswa bimbingan</Form.Label>
              <Form.Select id="reset-student" value={studentId} disabled={queueLoading || Boolean(busyKey)} onChange={(event) => setStudentId(event.target.value)} className="mb-3">
                <option value="">-- Pilih siswa --</option>{resetStudents.map((student) => <option key={student.id} value={student.id}>{student.nama} • {student.kelas} • NISN {student.nisn}</option>)}
              </Form.Select>
              {selectedStudentData && <Card className="mb-3" style={cardStyle}><Card.Body><small className="text-muted">DUDI / Tempat PKL</small><div className="fw-bold">{selectedStudentData.tempat_pkl || 'Belum ditempatkan'}</div><small className="text-muted">Satu sesi login aktif; login terbaru mengakhiri sesi sebelumnya.</small></Card.Body></Card>}
              <div className="d-grid gap-3">
                <Card style={cardStyle}><Card.Body><strong>Reset Password</strong><p className="small text-muted">Buat kata sandi sementara dan cabut semua sesi siswa.</p><Button className="w-100" disabled={!selectedStudentData || queueLoading || Boolean(busyKey)} onClick={async () => { if (!window.confirm('Reset password dan keluarkan semua sesi siswa ini?')) return; const result = await runAction('reset-password', () => resetStudentPassword(studentId), 'Password siswa direset.'); if (result?.data?.temporary_password) setMessage({ type: 'warning', text: `Password sementara: ${result.data.temporary_password} — bagikan langsung kepada siswa.` }); }}>Reset Password</Button></Card.Body></Card>
                <Card style={cardStyle}><Card.Body><strong>Reset Jadwal</strong><p className="small text-muted">Hapus jadwal presensi agar siswa mengisi ulang.</p><Button variant="outline-primary" className="w-100" disabled={!selectedStudentData || queueLoading || Boolean(busyKey)} onClick={() => { if (window.confirm('Hapus seluruh jadwal presensi siswa ini?')) runAction('reset-schedule', () => resetStudentSchedule(studentId), 'Jadwal berhasil direset.'); }}>Reset Jadwal</Button></Card.Body></Card>
                <Card style={cardStyle}><Card.Body><strong>Sesi Login Siswa</strong><p className="small text-muted">Siswa dapat login di perangkat mana pun tanpa reset device. Cabut sesi aktif jika diperlukan.</p><Button variant="danger" className="w-100" disabled={!selectedStudentData || queueLoading || Boolean(busyKey)} onClick={() => { if (window.confirm('Keluarkan sesi aktif siswa? Siswa perlu login ulang.')) runAction('move-device', () => moveStudentDevice(studentId), 'Sesi login siswa dicabut.'); }}>Keluarkan Sesi Siswa</Button></Card.Body></Card>
              </div>
            </>}
          </>
        )}

        {activeTab === 'akun' && <div className="guru-account-content">
          <Card className="guru-account-profile-card" style={cardStyle}>
            <Card.Body>
              <div className="guru-account-summary">
                <div className="guru-account-avatar">{(profile.nama_guru || 'G').split(/\s+/).slice(0, 2).map((item) => item[0]).join('').toUpperCase()}</div>
                <h5>{profile.nama_guru || 'Guru Pembimbing'}</h5>
                <p>Guru Pembimbing PKL • SMK Negeri Gudo</p>
                <span className="guru-account-active"><BsShieldCheck /> Akun Aktif</span>
              </div>
              <div className="guru-account-divider" />
              <AccountInfoRow icon={BsPerson} label="NIP" value={profile.nip || '—'} />
              <AccountInfoRow icon={BsEnvelope} label="Email" value={profile.email || 'Belum terdaftar'} unavailable={!profile.email} />
              <AccountInfoRow icon={BsTelephone} label="Nomor HP" value={profile.nomor_hp || profile.no_hp || 'Belum terdaftar'} unavailable={!profile.nomor_hp && !profile.no_hp} />
            </Card.Body>
          </Card>

          <section className="guru-account-school-section" aria-labelledby="guru-school-heading">
            <h6 id="guru-school-heading">Informasi Sekolah</h6>
            <Card className="guru-account-school-card" style={cardStyle}>
              <Card.Body>
                <AccountInfoRow icon={BsBuilding} label="Satuan Pendidikan" value="SMK Negeri Gudo" />
                <AccountInfoRow icon={BsGeoAlt} label="Alamat" value="Kecamatan Gudo, Kabupaten Jombang" />
                <AccountInfoRow icon={BsBank} label="Instansi" value="Dinas Pendidikan Provinsi Jawa Timur" />
              </Card.Body>
            </Card>
          </section>

          <Button type="button" variant="light" className="guru-account-change-password" onClick={openPasswordModal}>
            <BsKey /> <span>Ubah Kata Sandi</span><span className="guru-account-chevron">›</span>
          </Button>
          <Button variant="danger" className="guru-account-logout" onClick={handleLogout}>↪ &nbsp; Logout</Button>
          <p className="guru-account-footer">E-Presensi PKL SMK Negeri Gudo</p>
        </div>}
      </div>

      <nav className="app-bottom-nav guru-bottom-nav" aria-label="Navigasi pembimbing">
        {[
          ['monitoring', 'Monitoring', <BsGrid size={20} />],
          ['setting', 'Setting Siswa', <BsPeople size={20} />],
          ['akun', 'Pengaturan Akun', <BsPerson size={20} />],
        ].map(([key, label, icon]) => <button key={key} type="button" className={`app-bottom-nav-item ${activeTab === key ? 'active' : ''}`} aria-current={activeTab === key ? 'page' : undefined} onClick={() => setActiveTab(key)}>{icon}<span>{label}</span></button>)}
      </nav>

      <Modal show={Boolean(permitPhoto)} onHide={closePermitPhoto} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Bukti {permitPhoto?.permit?.jenis_izin === 'sakit' ? 'Sakit' : 'Izin'} — {permitPhoto?.permit?.siswa?.nama}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center" style={{ background: '#17202d' }}>
          {permitPhoto && <img src={permitPhoto.url} alt={`Lampiran bukti ${permitPhoto.permit?.jenis_izin || 'izin'}`} style={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain' }} />}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPasswordModal}
        onHide={closePasswordModal}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="guru-password-modal"
      >
        <Form onSubmit={handleChangePassword}>
          <Modal.Header>
            <Modal.Title className="fs-6">Ubah Kata Sandi</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {passwordError && <Alert variant="danger" className="py-2">{passwordError}</Alert>}
            <PasswordField
              label="Kata sandi saat ini"
              value={currentPassword}
              visible={passwordVisibility[0]}
              autoComplete="current-password"
              onChange={(event) => setCurrentPassword(event.target.value)}
              onToggle={() => setPasswordVisibility(([current, next, confirm]) => [!current, next, confirm])}
            />
            <PasswordField
              label="Kata sandi baru (minimal 8 karakter)"
              value={newPassword}
              visible={passwordVisibility[1]}
              autoComplete="new-password"
              onChange={(event) => setNewPassword(event.target.value)}
              onToggle={() => setPasswordVisibility(([current, next, confirm]) => [current, !next, confirm])}
            />
            <PasswordField
              label="Ulangi kata sandi baru"
              value={confirmPassword}
              visible={passwordVisibility[2]}
              autoComplete="new-password"
              onChange={(event) => setConfirmPassword(event.target.value)}
              onToggle={() => setPasswordVisibility(([current, next, confirm]) => [current, next, !confirm])}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={closePasswordModal} disabled={busyKey === 'password'}>Batal</Button>
            <Button type="submit" disabled={busyKey === 'password'}>
              {busyKey === 'password' ? <><Spinner size="sm" className="me-2" />Menyimpan…</> : 'Simpan'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </main>
  );
}

export default GuruPortal;
